import { useState } from 'react';
import PropTypes from 'prop-types';
import { bandOfRatio } from './microSimBands';

/**
 * One dot per decision, coloured by the share of points it earned. Tap a dot to see
 * the reply and the feedback note — keeps the report short on a phone instead of a
 * wall of text.
 */
const DecisionDots = ({ decisions, outcomes = [] }) => {
  const [open, setOpen] = useState(null);
  const rounds = [...new Set(decisions.map((d) => d.round))];
  const keyOf = (d) => `${d.round}-${d.step}`;
  const selected = decisions.find((d) => keyOf(d) === open);

  return (
    <div>
      <div className="space-y-3">
        {rounds.map((round) => {
          const inRound = decisions.filter((d) => d.round === round);
          const outcome = outcomes.find((o) => o.round === round);
          return (
            <div key={round}>
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-xs font-bold uppercase tracking-wide text-neutral-500">
                  Round {round + 1} · {inRound[0]?.roundTitle}
                </p>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {inRound.map((d, i) => {
                  const b = bandOfRatio(d.possible ? d.earned / d.possible : null);
                  const isOpen = open === keyOf(d);
                  return (
                    <button
                      key={keyOf(d)}
                      type="button"
                      onClick={() => setOpen(isOpen ? null : keyOf(d))}
                      aria-label={`Decision ${i + 1}: ${d.prompt}, ${d.earned} of ${d.possible} points`}
                      aria-pressed={isOpen}
                      className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white transition ${isOpen ? 'ring-2 ring-neutral-900 ring-offset-2' : ''}`}
                      style={{ backgroundColor: b.hex }}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
              {outcome && <p className="mt-1.5 text-xs text-primary-700">Outcome: {outcome.title}</p>}
            </div>
          );
        })}
      </div>

      {selected ? (
        <div className="mt-3 rounded-xl border border-neutral-200 bg-white p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{selected.prompt}</p>
          <p className="mt-1 text-sm text-neutral-900">“{selected.text}”</p>
          {selected.note && <p className="mt-1 text-xs text-neutral-600">{selected.note}</p>}
          <p className="mt-1 text-xs font-semibold text-neutral-400">{selected.earned} of {selected.possible} points</p>
        </div>
      ) : (
        <p className="mt-3 text-xs text-neutral-400">Tap a dot to see what you said and why it scored that way.</p>
      )}
    </div>
  );
};

DecisionDots.propTypes = {
  decisions: PropTypes.arrayOf(PropTypes.shape({
    round: PropTypes.number.isRequired,
    step: PropTypes.number.isRequired,
    roundTitle: PropTypes.string,
    prompt: PropTypes.string,
    text: PropTypes.string,
    note: PropTypes.string,
    earned: PropTypes.number,
    possible: PropTypes.number,
  })).isRequired,
  outcomes: PropTypes.array,
};

export default DecisionDots;
